import React, { useEffect, useState } from "react";
import Loader from "./Loader";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { displayToast } from "../utils/displayToast";
import { fetchFieldMaster, deleteFieldmaster } from "../apis/fieldMaster";
import { useErrorHandler } from "../utils/ErrorHandler";
import { capitalizeFirstLetterOfEachWord } from "../utils/Formatter";
import "../styles/components/FieldMasterlList.css";
import "../styles/components/PageStyles.css";
import Search from "../assets/svgIcons/Search.svg";
import DropDown from "../assets/svgIcons/PrimaryDropDown.svg";
import { useOutsideClick } from "../utils/useOutsideClick";

const FieldMasterlList = () => {
  const itemsPerPage = 8;
  const navigate = useNavigate();
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const { handleErrorResponse } = useErrorHandler();
  const [fieldMasterData, setFieldMasterData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchParams, setSearchParams] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);

  const filters = [
    {
      title: "tran_code",
      display_name: "Transaction Code",
    },
    {
      title: "field_name",
      display_name: "Field Name",
    },
    {
      title: "title",
      display_name: "Title",
    },
    {
      title: "status",
      display_name: "Status",
    },
  ];

  const dropdownRef = useOutsideClick(() => {
    setIsDropdownOpen(false);
  });

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsDropdownOpen(false);
  };

  const handleSearch = () => {
    if (!searchValue) {
      setSearchParams({});
      setCurrentPage(0);
      return;
    }
    if (!selectedCategory.title) {
      displayToast("Category not selected", "error");
    } else if (!searchValue) {
      displayToast("Please enter a search value", "error");
    } else {
      setSearchParams({
        field: selectedCategory.title,
        value: searchValue,
      });
      setCurrentPage(0);
    }
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleEdit = (masterData) => {
    navigate(`/field-master/edit/${masterData.id}`, {
      state: { masterData },
    });
  };

  const handleView = (item) => {
    setViewingItem(item);
  };

  const closeViewModal = () => {
    setViewingItem(null);
  };

  const handleCreateNew = () => {
    navigate("/field-master/new");
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "active") return "mfg-status-active";
    if (statusLower === "inactive") return "mfg-status-inactive";
    if (statusLower === "pending") return "mfg-status-pending";
    return "mfg-status-info";
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      const response = await deleteFieldmaster(itemToDelete.id);
      if (response.status === "success") {
        displayToast("Field master deleted successfully", "success");
        setFieldMasterData((prevData) =>
          prevData.filter((item) => item.id !== itemToDelete.id),
        );
      } else if (response.description) {
        displayToast(response.description, "error");
      }
    } catch (error) {
      displayToast("Error deleting field master", "error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  useEffect(() => {
    fetchFieldMasterData(searchParams);
  }, [currentPage, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFieldMasterData = async (searchParams = {}) => {
    setIsLoading(true);
    try {
      let query = `?count=${itemsPerPage}&page=${currentPage + 1}`;

      if (searchParams.field && searchParams.value) {
        query += `&${searchParams.field}=${searchParams.value}`;
      }

      const response = await fetchFieldMaster(query);

      if (response && response.items) {
        setFieldMasterData(response.items);
        if (response.total_count) {
          setPageCount(Math.ceil(response.total_count / itemsPerPage));
        } else {
          setPageCount(1);
        }
      } else if (response && response.description) {
        handleErrorResponse(response.description);
        setFieldMasterData([]);
        setPageCount(1);
      } else {
        displayToast(
          "Unexpected response while fetching field master data",
          "error",
        );
        setFieldMasterData([]);
        setPageCount(1);
      }
    } catch (error) {
      displayToast("Error fetching field master data", "error");
      setFieldMasterData([]);
      setPageCount(1);
    } finally {
      setIsLoading(false);
    }
  };

  const tableHeaders = [
    { key: "tran_code", label: "Transaction Code" },
    { key: "field_name", label: "Field Name" },
    { key: "title", label: "Title" },
    { key: "status", label: "Status" },
  ];

  const tableHeadersWithAction = [
    ...tableHeaders,
    { key: "action", label: "Action" },
  ];

  return (
    <div className="mfg-page-container">
      <div className="mfg-page-header">
        <div className="mfg-page-title-section">
          <h2 className="mfg-page-title">
            <span className="mfg-page-title-icon">⚙️</span>
            Field Master Configuration
          </h2>
          <p className="mfg-page-subtitle">
            Manage field configurations for transactions
          </p>
        </div>
        <div className="mfg-header-actions">
          <button
            className="mfg-btn mfg-btn-outline"
            onClick={() => navigate("/field-master/projections")}
          >
            <span className="mfg-btn-icon">🔗</span>
            Projections
          </button>
          <button className="mfg-btn mfg-btn-primary" onClick={handleCreateNew}>
            <span className="mfg-btn-icon">➕</span>
            Create New Field Master
          </button>
        </div>
      </div>

      <div className="mfg-card">
        <div className="mfg-card-header">
          <h3 className="mfg-card-title">
            <span className="mfg-card-title-icon">📋</span>
            Field Master Data List
          </h3>
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="mfg-filters">
              <div className="mfg-filter-dropdown" ref={dropdownRef}>
                <div
                  className="mfg-filter-dropdown-btn"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span>
                    {selectedCategory.display_name || "Select Category"}
                  </span>
                  <img
                    src={DropDown}
                    alt="dropdown"
                    className="mfg-filter-dropdown-icon"
                  />
                </div>

                {isDropdownOpen && (
                  <div className="mfg-filter-dropdown-menu">
                    {filters.map((filter, index) => (
                      <div
                        key={index}
                        className="mfg-filter-dropdown-item"
                        onClick={() => handleCategorySelect(filter)}
                      >
                        {filter.display_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mfg-search-box">
                <input
                  type="text"
                  placeholder="Search field master..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="mfg-search-input"
                />
                <img
                  src={Search}
                  alt="search"
                  className="mfg-search-icon"
                  onClick={handleSearch}
                />
              </div>
            </div>

            {fieldMasterData.length > 0 ? (
              <div className="mfg-table-wrapper">
                <table className="mfg-table">
                  <thead>
                    <tr>
                      {tableHeadersWithAction.map((header) => (
                        <th key={header.key}>{header.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fieldMasterData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <strong>
                            {capitalizeFirstLetterOfEachWord(
                              String(item.tran_code),
                            )}
                          </strong>
                        </td>
                        <td>
                          {capitalizeFirstLetterOfEachWord(
                            String(item.field_name),
                          )}
                        </td>
                        <td>
                          {capitalizeFirstLetterOfEachWord(String(item.title))}
                        </td>
                        <td>
                          <span
                            className={`mfg-status-badge ${getStatusBadgeClass(item.status)}`}
                          >
                            {capitalizeFirstLetterOfEachWord(
                              String(item.status),
                            )}
                          </span>
                        </td>
                        <td>
                          <div className="mfg-table-actions">
                            <button
                              className="mfg-btn mfg-btn-outline mfg-btn-sm"
                              onClick={() => handleView(item)}
                            >
                              👁️ <span className="mfg-btn-text">View</span>
                            </button>
                            <button
                              className="mfg-btn mfg-btn-warning mfg-btn-sm"
                              onClick={() => handleEdit(item)}
                            >
                              ✏️ <span className="mfg-btn-text">Edit</span>
                            </button>
                            <button
                              className="mfg-btn mfg-btn-danger mfg-btn-sm"
                              onClick={() => handleDeleteClick(item)}
                            >
                              🗑️ <span className="mfg-btn-text">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mfg-empty-state">
                <div className="mfg-empty-icon">⚙️</div>
                <h4 className="mfg-empty-title">No Field Master Data Found</h4>
                <p className="mfg-empty-text">
                  Create a new field master configuration to get started
                </p>
              </div>
            )}
          </>
        )}

        {pageCount > 0 && !isLoading && (
          <ReactPaginate
            breakLabel="..."
            previousLabel={<ChevronLeft size={20} />}
            nextLabel={<ChevronRight size={20} />}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={handlePageClick}
            breakClassName="pagination-box"
            containerClassName="pagination"
            pageClassName="pagination-box"
            pageLinkClassName="pagination-link"
            previousClassName="pagination-box"
            nextClassName="pagination-box"
            previousLinkClassName="pagination-link"
            nextLinkClassName="pagination-link"
            activeClassName="pagination-active"
            disabledClassName="pagination-disabled"
            breakLinkClassName="pagination-link"
          />
        )}
      </div>

      {viewingItem && (
        <div className="mfg-modal-overlay" onClick={closeViewModal}>
          <div className="mfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mfg-modal-header">
              <h3 className="mfg-modal-title">
                <span>⚙️</span> Field Master Details
              </h3>
              <button className="mfg-modal-close" onClick={closeViewModal}>
                ✕
              </button>
            </div>
            <div className="mfg-modal-body">
              <div className="mfg-view-grid">
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Transaction Code</div>
                  <div className="mfg-view-value">{viewingItem.tran_code}</div>
                </div>
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Field Name</div>
                  <div className="mfg-view-value">{viewingItem.field_name}</div>
                </div>
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Title</div>
                  <div className="mfg-view-value">{viewingItem.title}</div>
                </div>
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Status</div>
                  <div className="mfg-view-value">
                    <span
                      className={`mfg-status-badge ${getStatusBadgeClass(viewingItem.status)}`}
                    >
                      {viewingItem.status}
                    </span>
                  </div>
                </div>
                {viewingItem.field_type && (
                  <div className="mfg-view-item">
                    <div className="mfg-view-label">Field Type</div>
                    <div className="mfg-view-value">
                      {viewingItem.field_type}
                    </div>
                  </div>
                )}
                {viewingItem.default_value && (
                  <div className="mfg-view-item">
                    <div className="mfg-view-label">Default Value</div>
                    <div className="mfg-view-value">
                      {viewingItem.default_value}
                    </div>
                  </div>
                )}
                {viewingItem.description && (
                  <div className="mfg-view-item mfg-view-full-width">
                    <div className="mfg-view-label">Description</div>
                    <div className="mfg-view-value">
                      {viewingItem.description}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mfg-modal-footer">
              <button
                className="mfg-btn mfg-btn-outline"
                onClick={closeViewModal}
              >
                Close
              </button>
              <button
                className="mfg-btn mfg-btn-warning"
                onClick={() => {
                  closeViewModal();
                  handleEdit(viewingItem);
                }}
              >
                ✏️ Edit Field Master
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="mfg-modal-overlay" onClick={handleDeleteCancel}>
          <div className="mfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mfg-modal-header">
              <h3 className="mfg-modal-title">
                <span>⚠️</span> Confirm Delete
              </h3>
              <button className="mfg-modal-close" onClick={handleDeleteCancel}>
                ✕
              </button>
            </div>
            <div className="mfg-modal-body">
              <p className="mfg-modal-confirm-text">
                Are you sure you want to delete this field master record?
              </p>
              {itemToDelete && (
                <div className="mfg-view-grid">
                  <div className="mfg-view-item">
                    <div className="mfg-view-label">Transaction Code</div>
                    <div className="mfg-view-value">
                      {itemToDelete.tran_code}
                    </div>
                  </div>
                  <div className="mfg-view-item">
                    <div className="mfg-view-label">Field Name</div>
                    <div className="mfg-view-value">
                      {itemToDelete.field_name}
                    </div>
                  </div>
                </div>
              )}
              <p className="mfg-modal-danger-text-mt">
                ⚠️ This action cannot be undone.
              </p>
            </div>
            <div className="mfg-modal-footer">
              <button
                className="mfg-btn mfg-btn-outline"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="mfg-btn mfg-btn-danger"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "🗑️ Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldMasterlList;
